import {
  Profile, ProfileSectionKey, ProfileStore, ProfileStoreKey,
} from '@/models/Profile';
import { User } from '@/models/User';
import { getOwnProfile, getProfile, getUser } from '@/services/Profile/Api';
import isEmptyDeep from '@/utils/isEmptyDeep';
import toggleArrayItem from '@/utils/toggleArrayItem';
import useProfilePreview, { isPreviewableSection, profileKeyForSection } from '@/state/profile/profilePreview';
import saveSectionPreview from '@/state/profile/previewSave';
import mergeProfileWithPreview from '@/state/profile/mergeProfilePreview';
import {
  computed, inject, onBeforeUnmount, provide, ref, Ref, watch
} from 'vue';
import { asyncComputed } from '@vueuse/core';
import { isEmpty } from 'lodash';
import { Route } from 'vue-router';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';
import { useStore } from 'vue2-helpers/vuex';
import useAddressTools from '@/mixins/useAddressTools';
import { isDeletedUser } from '@/utils/user';
import hasVisibleRichTextContent from '@/views/profile/utils/hasVisibleRichTextContent';

const me = ref<User | null>(null);
const routerUserId = ref<string | null>(null);
const loadProfileRequestIds = {
  [ProfileStoreKey.PROFILE]: 0,
  [ProfileStoreKey.QUICK_INFO]: 0,
};

const store:ProfileStore = {
  [ProfileStoreKey.PROFILE]: {
    profile: ref(null),
    userId: ref(''),
    isSelf: ref(false),
    user: asyncComputed(async () => {
      if (store.profile.isSelf.value) return me.value;
      return store.profile.profile.value ? getUser(store.profile.userId.value) : null;
    }, null, { lazy: true }),
    isLoading: ref(false),
  },
  [ProfileStoreKey.QUICK_INFO]: {
    profile: ref(null),
    userId: ref(''),
    isSelf: ref(false),
    user: ref(null),
    isLoading: ref(false),
  }
};

const editSectionsByScope = ref<Record<string, string[]>>({});

const ensureEditSectionsScope = (scope: string) => {
  if (!editSectionsByScope.value[scope]) {
    editSectionsByScope.value = {
      ...editSectionsByScope.value,
      [scope]: [],
    };
  }
  return editSectionsByScope.value[scope];
};

watch(store.profile.isSelf, (is) => {
  if (!is) editSectionsByScope.value = {};
});

const loadProfile = async (
  storeKey:ProfileStoreKey,
  { silent = false }: { silent?: boolean } = {},
) => {
  const requestId = ++loadProfileRequestIds[storeKey];
  const previousExperiencesDocument = store[storeKey].profile.value?.experiences?.experiencesDocument ?? null;
  const previousExperiencesDocumentLabel = store[storeKey].profile.value?.experiences?.experiencesDocumentLabel ?? null;

  if (!silent) {
    store[storeKey].isLoading.value = true;
  }

  try {
    const doGetOwnProfile = storeKey === ProfileStoreKey.PROFILE && store.profile.isSelf.value;

    let profile = doGetOwnProfile ? await getOwnProfile() : await getProfile(store[storeKey].userId.value);

    if (requestId !== loadProfileRequestIds[storeKey]) return;

    if (!profile) {
      if (silent) {
        throw new Error('Profile reload returned empty result');
      }

      store[storeKey].profile.value = null;
      return;
    }

    if (previousExperiencesDocument && profile.experiences) {
      profile = {
        ...profile,
        experiences: {
          ...profile.experiences,
          experiencesDocument: previousExperiencesDocument,
          experiencesDocumentLabel: previousExperiencesDocumentLabel ?? profile.experiences.experiencesDocumentLabel,
        },
      };
    }

    store[storeKey].profile.value = profile;
  } finally {
    if (!silent && requestId === loadProfileRequestIds[storeKey]) {
      store[storeKey].isLoading.value = false;
    }
  }
};

// Accept an AI preview for a section: persist it via the matching save
// endpoint, refetch the (now-canonical) saved profile, then clear the slot.
// Throws if the save fails so the slot survives for retry; refetch errors
// also leave the slot intact since the user's intent ("save this") hasn't
// been confirmed end-to-end. Only the self-owned PROFILE store can accept.
const acceptSection = async (section: ProfileSectionKey): Promise<void> => {
  if (!isPreviewableSection(section)) return;
  const { previewProfile, discardSection } = useProfilePreview();
  const profileKey = profileKeyForSection(section);
  const previewValue = previewProfile.value[profileKey];
  if (previewValue === undefined) return;

  await saveSectionPreview(section, previewProfile.value);
  if (store.profile.profile.value) {
    store.profile.profile.value = {
      ...store.profile.profile.value,
      [profileKey]: previewValue,
    };
  }
  try {
    await loadProfile(ProfileStoreKey.PROFILE, { silent: true });
  } catch (error) {
    const reloadError = error instanceof Error ? error : new Error('Profile reload after accept failed');
    (reloadError as Error & { savedButReloadFailed?: boolean }).savedButReloadFailed = true;
    throw reloadError;
  }
  discardSection(section);
};

const init = (storeKey:ProfileStoreKey, userParam?:Ref<User>) => {
  const route = useRoute();
  const router = useRouter();
  const vuexStore = useStore();

  provide('storeKey', storeKey);

  if (storeKey === ProfileStoreKey.PROFILE) {
    me.value = vuexStore.getters['user'];
    const setUserByRoute = (route:Route | undefined) => {
      routerUserId.value = route?.params?.userId ? encodeURIComponent(route.params.userId) : null;
      store[storeKey].userId.value = routerUserId.value ?? me.value?.userId ?? '??';
      store[storeKey].isSelf.value = !routerUserId.value || routerUserId.value === me.value?.userId;
    };

    setUserByRoute(route);

    const stopGuard = router.beforeEach(async (to, from, next) => {
      setUserByRoute(to);
      await loadProfile(storeKey).then();
      next();
    });

    onBeforeUnmount(() => {
      stopGuard();
    });
  } else {
    store[storeKey].user.value = userParam?.value ?? null;
    store[storeKey].userId.value = userParam?.value?.userId ?? '';
    store[storeKey].isSelf.value = store[storeKey].userId.value === vuexStore.getters['user'].userId;
  }

  loadProfile(storeKey).then();
};

const getVisibleCompanyMembers = (members?: User[]) => members?.filter((member) => member.companyRole !== 'ROOT' && !isDeletedUser(member)) ?? [];

const useProfileState = (storeKeyInit?:ProfileStoreKey, user?:Ref<User>, editScopeInit?: string) => {
  const { countries } = useAddressTools();
  const { previewProfile } = useProfilePreview();
  const storeKey = inject<ProfileStoreKey | null>('storeKey', null) || storeKeyInit as ProfileStoreKey;
  const editScope = inject<string | null>('profileEditScope', null) || editScopeInit || storeKey;

  if (!storeKey) throw new Error('Storekey not provided! Can\'t provide profileState without storekey.');

  provide('profileEditScope', editScope);

  const editSections = computed(() => ensureEditSectionsScope(editScope));

  const toggleEditSection = (sectionName:string) => {
    toggleArrayItem(ensureEditSectionsScope(editScope), sectionName);
  };

  const closeEditSection = (section:string) => {
    const sections = ensureEditSectionsScope(editScope);
    const idx = sections.indexOf(section);
    if (idx >= 0) sections.splice(idx, 1);
  };

  const isEditSectionOpen = (section:string) => ensureEditSectionsScope(editScope).includes(section);

  const patchSavedProfile = (patch: Partial<Profile>) => {
    const currentProfile = store[storeKey].profile.value;
    if (!currentProfile) return;

    store[storeKey].profile.value = {
      ...currentProfile,
      ...patch,
    };
  };

  // AI preview overlay: a section's preview slot (if present) wins over saved
  // profile data. Consumers read effectiveProfile and don't see the merge.
  // Quick-info reads saved data only — previews apply to the owner's editor flow.
  const effectiveProfile = computed<Profile | null>(() => {
    const saved = store[storeKey].profile.value;
    if (!saved) return null;
    if (storeKey !== ProfileStoreKey.PROFILE || !store[storeKey].isSelf.value) return saved;
    const preview = previewProfile.value;
    return mergeProfileWithPreview(saved, preview);
  });

  const isSectionFilled = computed<Record<ProfileSectionKey, boolean>>(() => {
    const p = effectiveProfile.value;
    const hasCertificates = !!p?.certificates?.certificatesList?.length;
    const hasEducations = !!p?.educations?.educationsList?.length;

    const certificatesEducations = (storeKey === ProfileStoreKey.PROFILE && store[storeKey].isSelf.value) || hasCertificates || hasEducations;

    return ({
      basic: !isEmpty(p?.basic),
      certificates: hasCertificates,
      educations: hasEducations,
      projectPreferences: !isEmptyDeep(p?.projectPreferences),
      skills: true,
      references: !isEmpty(p?.references?.referencesList),
      projects: !!(
        p?.experiences?.experiencesDocumentLabel
        || hasVisibleRichTextContent(p?.experiences?.experiencesText)
        || p?.experiences?.experiencesDocument
      ),
      certificatesEducations,
      invitedBy: !isEmpty(p?.basic?.invitedBy),
      aboutus: !!(p?.company?.employeeCount || p?.company?.industries?.length || p?.company?.aboutUs?.length),
      members: !!getVisibleCompanyMembers(p?.company?.members).length,
      publicprojects: !!p?.company?.publicProjects?.externalProjectList?.length,
      company: !isEmpty(p?.company)
    });
  });

  const isQuickInfo = storeKey === ProfileStoreKey.QUICK_INFO;

  const canEdit = computed(() => {
    const self = store[storeKey].isSelf.value;
    return self && !isQuickInfo;
  });

  return ({
    isSectionFilled,
    init: () => { init(storeKey, user); },
    editSections,
    countries,
    toggleEditSection,
    loadProfile: (options?: { silent?: boolean }) => loadProfile(storeKey, options),
    patchSavedProfile,
    closeEditSection,
    isEditSectionOpen,
    storeKey,
    isQuickInfo,
    ...store[storeKey],
    // Override `profile` from the spread with the preview-merged view.
    // Original saved ref still available as `savedProfile` for callers that
    // need to compare against pre-preview state (e.g. isSectionFilled checks
    // that drive the upload banner — those must not see previews as "saved").
    savedProfile: store[storeKey].profile,
    profile: effectiveProfile,
    canEdit,
    acceptSection,
  });
};

export default useProfileState;
